import { AIBlockConfig } from "../ai-block-types";
import { ModelBoardDependencyMapResponse } from "shared/ai-model/responses/board-dependecy-map-schema";
import { TemplateData } from "../templates/utils";
import { placeShape } from "shared/datamodel/shape";
import { getAnchorSide } from "utils/math-utils";
import { placeConnector } from "shared/datamodel/connector";
import { getDefaultSizeForShape } from "utils/shape-utils";
import consts from "shared/consts";
import { getUnixTimestampUTC } from "shared/util/utils";
import { NewElementData } from "shared/datamodel/canvas-element";
import { centerElementsAroundOrigin } from "utils/template-utils";
import { placeTextBlock } from "shared/datamodel/textBlock";
import { newGroupId } from "frontend/canvas-designer-new/elements/canvas-elements-utils";
import { ElementRuleComparator } from "shared/datamodel/schemas/element-data-layer";
import { getStatuses } from "utils/monday-utils";
import { nanoid } from "nanoid";
import { Sizes } from "frontend/data/shapes/sizes";

const createRules = (
  integrationId: string,
  mondayItemId: string,
  statuses: Array<{
    title: string;
    color: string;
  }>,
  statusColId?: string
) => {
  if (!statusColId) {
    return [];
  }

  const fillRules = statuses.map((status) => {
    return {
      id: nanoid(10),
      itemId: `integration:${integrationId}:${mondayItemId}`,
      fieldId: `integration:${integrationId}:${mondayItemId}:${statusColId}`,
      comparator: ElementRuleComparator.eq,
      compareValue: status.title,
      action: {
        property: "fill",
        value: status.color,
      },
      active: true,
    };
  });

  return fillRules;
};
async function dependencyMapToElements(response: ModelBoardDependencyMapResponse): Promise<NewElementData[]> {
  const elements: TemplateData = {};
  const nodeIdMap = new Map<string, string>(); // AI node ID -> canvas element ID
  const sizes = Sizes.get();

  // Find the first status column (by type, not by id)
  const statusCol = response.columns.find((c) => c.type === "status");
  const statuses = getStatuses(statusCol);

  // Create a shape element for each node
  for (const node of response.tasks) {
    const groupId = `derived:${newGroupId()}`;
    const position = { x: node.xPos, y: node.yPos };

    const shapeType = node.label.toLowerCase().includes("milestone") ? "octagon" : "rounded-rect";
    const { width } = getDefaultSizeForShape(shapeType);
    const lineHeight = 20;

    const shape = placeShape({
      point: position,
      init: { type: "shape", subtype: shapeType },
      actualSize: getDefaultSizeForShape(shapeType),
      defaultShapeSize: getDefaultSizeForShape(shapeType),
      rotate: 0,
      sizes: {
        strokeWidth: 0,
        fontSize: 0,
        defaultRadius: 100,
      },
    });
    const shapeId = `${consts.CANVAS_ELEMENTS.SHAPE}-${shape.id}`;

    nodeIdMap.set(node.id, shapeId);

    const scaleX = shapeType === "rounded-rect" ? 1.088 : 1;
    const scaleY = shapeType === "rounded-rect" ? 0.5939 : 1;

    const shapeElement = {
      ...shape.shape,
      scaleX,
      scaleY,
      groupId,
      groupHistory: [],
      fill: "#8949CFff",
      border: "transparent",
      zIndexLastChangeTime: getUnixTimestampUTC(),
      attachedConnectors: {}, // Initialize empty, will be populated below
      fontSize: 30,
      strokeWidth: shapeType === "octagon" ? 5 : 2,
    };

    // Place text label on shape

    const integrationId = node.isSubitem ? response.subitemsBoardIntegrationId : response.boardIntegrationId;
    if (!integrationId) {
      continue;
    }

    const dynamicText = `\${${shapeId}:integration:${integrationId}:${node.mondayItemId}:name}`;
    const { id, textBlock } = placeTextBlock(
      { width, height: lineHeight, groupId, groupHistory: [], derivedElementIds: [shapeId], textColor: "#ffffff" },
      { x: position.x - width / 2, y: position.y - lineHeight / 2 },
      dynamicText
    );
    const textBlockId = `${consts.CANVAS_ELEMENTS.TEXT_BLOCK}-${id}`;

    shapeElement["dataLayer"] = {
      fieldsOrder: [],
      fields: {},
      values: {},
      rules: createRules(integrationId, node.mondayItemId, statuses, statusCol?.id),
      integrations: {
        [integrationId]: {
          [node.mondayItemId]: {
            columnIds: "all",
          },
        },
      },
      elementMapping: {
        [`integration:${integrationId}:${node.mondayItemId}:name`]: textBlockId,
      },
    };

    elements[shapeId] = shapeElement;
    elements[textBlockId] = textBlock;
  }

  // Create connector elements for each edge
  const connectorAttachments = new Map<string, { lineId: string; index: number }[]>();

  for (const edge of response.dependencies) {
    const sourceElementId = nodeIdMap.get(edge.source);
    const targetElementId = nodeIdMap.get(edge.target);

    if (!sourceElementId || !targetElementId) {
      continue;
    }

    const connectedToSubitem =
      response.tasks.find((t) => t.id === edge.source)?.isSubitem ||
      response.tasks.find((t) => t.id === edge.target)?.isSubitem;

    const connector = placeConnector(
      {
        lineType: "elbow",
        strokeWidth: sizes.connector.strokeWidth.default,
        stroke: "#A6AAAD",
        startArrow: "none",
        endArrow: connectedToSubitem ? "none" : "arrow",
      },
      { x: 0, y: 0 } // Connectors position themselves based on connected shapes
    );

    const connectorElement = {
      ...connector.connector,
      connectedShapes: [
        { id: sourceElementId, type: consts.CANVAS_ELEMENTS.SHAPE },
        { id: targetElementId, type: consts.CANVAS_ELEMENTS.SHAPE },
      ],
      anchorsMode: [getAnchorSide(edge.sourceAnchor), getAnchorSide(edge.targetAnchor)],
      anchorIndexes: [0, 1],
      zIndexLastChangeTime: -getUnixTimestampUTC(),
    };

    const connectorId = `${consts.CANVAS_ELEMENTS.CONNECTOR}-${connector.id}`;

    elements[connectorId] = connectorElement as TemplateData[typeof connectorId];

    // Track connector attachments for each shape
    if (!connectorAttachments.has(sourceElementId)) {
      connectorAttachments.set(sourceElementId, []);
    }
    if (!connectorAttachments.has(targetElementId)) {
      connectorAttachments.set(targetElementId, []);
    }

    const currentSourceAttachments = connectorAttachments.get(sourceElementId);
    const currentTargetAttachments = connectorAttachments.get(targetElementId);

    currentSourceAttachments?.push({ lineId: connectorId, index: 0 });
    currentTargetAttachments?.push({ lineId: connectorId, index: 1 });
  }

  // Now update all shape elements with their attached connectors
  for (const [id, element] of Object.entries(elements)) {
    if (element.type === consts.CANVAS_ELEMENTS.SHAPE) {
      const attachments = connectorAttachments.get(id);
      if (attachments && attachments.length > 0) {
        const attachedConnectors: Record<string, { lineId: string; index: number }> = {};
        for (const attachment of attachments) {
          attachedConnectors[attachment.lineId] = attachment;
        }
        elements[id].attachedConnectors = attachedConnectors;
      }
    }
  }

  const result = new Map(Object.entries(centerElementsAroundOrigin(elements)));
  return result as unknown as NewElementData[];
}

export const boardDependencyMapBlock: AIBlockConfig = {
  name: "board-dependency-map",
  blockType: "generate",
  responseToElements: async (response) => ({
    elements: await dependencyMapToElements(response),
  }),
  defaultPosition: { x: 200, y: 200 },
  shouldCreateIntegration: true,
};
